import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { CloseOutlined } from "@ant-design/icons";
import "../css/InviteUserDialog.css";
import { FBO_CUSTOMER_ROLE } from "../../utils/constants";
import { validateEmail } from "../../utils/utils";
import useFBOApi from "../../services/api/useFBOApi";
import { useMutation } from "@tanstack/react-query";

const InviteUserDialog = ({ open, onClose, onClickAdd, isLoading }) => {
  const { searchFBOProfile } = useFBOApi();
  const [isAddingManually, setIsAddingManually] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedFBOType, setSelectedFBOType] = useState(null);
  const [addingUserProfile, setAddingUserProfile] = useState(null);

  const searchFBOProfileMutation = useMutation({
    mutationFn: ({ data }) => searchFBOProfile(data),
    onSuccess: (response) => {
      setAddingUserProfile(response);
      setFirstName(response?.data?.firstName);
      setLastName(response?.data?.lastName);
      setIsAddingManually(true);
    },
    onError: () => {
      setIsAddingManually(true);
    },
  });

  useEffect(() => {
    setDefaultValue();
  }, [open]);

  const setDefaultValue = () => {
    setAddingUserProfile(null);
    setIsAddingManually(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setSelectedFBOType(null);
  };

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleFBOTypeChange = (event) => {
    setSelectedFBOType(event.target.value);
  };

  const handleAddClick = () => {
    if (!isAddingManually) {
      searchFBOProfileMutation.mutate({
        data: {
          email: email,
        },
      });
    } else {
      onClickAdd(firstName, lastName, email, selectedFBOType);
    }
  };

  const isValidToAdd = () => {
    return isAddingManually
      ? !firstName.trim() || !lastName.trim() || !selectedFBOType
      : !email.trim() || !validateEmail(email);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        className: isAddingManually
          ? "invite-user-manually-dialog-container"
          : "invite-user-dialog-container",
        sx: { borderRadius: "20px" },
      }}
    >
      <DialogContent>
        <CloseOutlined className="invite-user-dialog-close" onClick={onClose} />
        <div className="invite-user-dialog-content">
          <div className="invite-user-dialog-title">Invite Member</div>
          {isAddingManually && (
            <>
              <div className="invite-user-input-container">
                <label>First Name</label>
                {addingUserProfile ? (
                  <div className="invite-user-non-editable-field">
                    {addingUserProfile?.data?.firstName}
                  </div>
                ) : (
                  <input
                    name="invite-user-first-name"
                    placeholder="Enter first name"
                    variant="outlined"
                    value={firstName}
                    onChange={handleFirstNameChange}
                  />
                )}
              </div>
              <div className="invite-user-input-container">
                <label>Last Name</label>
                {addingUserProfile ? (
                  <div className="invite-user-non-editable-field">
                    {addingUserProfile?.data?.lastName}
                  </div>
                ) : (
                  <input
                    name="invite-user-last-name"
                    placeholder="Enter last name"
                    variant="outlined"
                    value={lastName}
                    onChange={handleLastNameChange}
                  />
                )}
              </div>
            </>
          )}
          <div className="invite-user-input-container">
            <label>Email</label>
            <input
              name="invite-user-email"
              placeholder="Enter email"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
            />
          </div>
          {isAddingManually && (
            <div className="invite-user-input-container">
              <label>Account Type</label>
              <Select
                id="invite-user-select-container"
                className="invite-user-dialog-select-container"
                value={selectedFBOType}
                onChange={handleFBOTypeChange}
                renderValue={(selected) =>
                  FBO_CUSTOMER_ROLE.find((type) => type.value === selected)
                    ?.name
                }
              >
                {FBO_CUSTOMER_ROLE.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <ListItemText primary={type.name} />
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={!isLoading ? handleAddClick : null}
            endIcon={
              isLoading || searchFBOProfileMutation?.isPending ? (
                <CircularProgress size={30} color="inherit" />
              ) : null
            }
            style={{ marginTop: "20px" }}
            disabled={isValidToAdd()}
          >
            Add
          </Button>
          {!isAddingManually && (
            <Button
              className="add-manually"
              onClick={() => setIsAddingManually(true)}
            >
              Add Manually
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserDialog;
